import { Field, Input, Label } from "@headlessui/react";
import { Loading } from "../../../../../components/Loading";
import RequiredField from "../../../../../components/RequiredField";
import Select from "../../../../../components/input/Select";
import { localeShortDateTime } from "../../../../../app/datetimeUtils";
import { DistrictClassLinkInfoResult } from "../../../districtsApiSlice";

interface ClassLinkSettingsFormProps {
	data?: DistrictClassLinkInfoResult;
	setData: (data: any) => void;
	isLoading: boolean;
}

export default function ClassLinkSettingsForm({
	data,
	setData,
	isLoading,
}: ClassLinkSettingsFormProps) {
	return (
		<div className="flex flex-col mt-3 gap-x-12 w-full">
			<p className="text-xs text-right leading-6 -mb-2 text-gray-600">
				<RequiredField className="!text-sm" /> - Denotes a required field
			</p>
			<Loading loading={isLoading} />
			{data && (
				<div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-6">
					<Field className="col-span-3">
						<Label htmlFor="tenantId" className="font-medium text-sm">
							Tenant ID
							<RequiredField />
						</Label>
						<Input
							id="tenantId"
							name="tenantId"
							type="text"
							required
							maxLength={100}
							className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
							value={(data as DistrictClassLinkInfoResult).tenantId ?? ""}
							onChange={(e) =>
								setData({
									...data,
									tenantId: e.target.value,
								})
							}
						/>
					</Field>
					<Field className="col-span-3">
						<Label htmlFor="classLinkId" className="font-medium text-sm">
							ClassLink ID
							<RequiredField />
						</Label>
						<Input
							id="classLinkId"
							name="classLinkId"
							type="text"
							required
							maxLength={100}
							className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
							value={(data as DistrictClassLinkInfoResult).classLinkId ?? ""}
							onChange={(e) =>
								setData({
									...data,
									classLinkId: e.target.value,
								})
							}
						/>
					</Field>
					<Field className="col-span-3">
						<Label htmlFor="clientId" className="font-medium text-sm">
							Client ID
							<RequiredField />
						</Label>
						<Input
							id="clientId"
							name="clientId"
							type="text"
							required
							maxLength={100}
							className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
							value={(data as DistrictClassLinkInfoResult).clientId ?? ""}
							onChange={(e) =>
								setData({
									...data,
									clientId: e.target.value,
								})
							}
						/>
					</Field>
					<Field className="col-span-3">
						<Label htmlFor="clientSecret" className="font-medium text-sm">
							Client Secret
							<RequiredField />
						</Label>
						<Input
							id="clientSecret"
							name="clientSecret"
							type="password"
							autoComplete="new-password"
							required
							maxLength={100}
							className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
							value={(data as DistrictClassLinkInfoResult).clientSecret ?? ""}
							onChange={(e) =>
								setData({
									...data,
									clientSecret: e.target.value,
								})
							}
						/>
					</Field>
					<Field className="col-span-6">
						<Label htmlFor="endpointUrl" className="font-medium text-sm">
							Endpoint URL
							<RequiredField />
						</Label>
						<Input
							id="endpointUrl"
							name="endpointUrl"
							type="text"
							required
							maxLength={255}
							className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
							value={(data as DistrictClassLinkInfoResult).endpointUrl ?? ""}
							onChange={(e) =>
								setData({
									...data,
									endpointUrl: e.target.value,
								})
							}
						/>
					</Field>
					<Select
						value={(data as DistrictClassLinkInfoResult).syncStatus ?? ""}
						setValue={(value) =>
							setData({
								...data,
								syncStatus: value,
							})
						}
						simpleListBoxId="syncStatus"
						options={[
							{ value: "disabled", label: "Disabled" },
							{ value: "waiting", label: "Waiting" },
							{ value: "inProgress", label: "In Progress", disabled: true },
						]}
						label={"Sync Status"}
						fieldClasses="col-span-6"
					/>
					<Field className="col-span-3">
						<Label htmlFor="syncStartTime" className="font-medium text-sm">
							Sync Start Time
						</Label>
						<Input
							id="syncStartTime"
							name="syncStartTime"
							type="text"
							readOnly
							className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
							value={
								(data as DistrictClassLinkInfoResult).syncStartTime == undefined
									? ""
									: localeShortDateTime(
											(data as DistrictClassLinkInfoResult).syncStartTime,
											true
										)
							}
						/>
					</Field>
					<Field className="col-span-3">
						<Label htmlFor="syncFinishTime" className="font-medium text-sm">
							Sync Finish Time
						</Label>
						<Input
							id="syncFinishTime"
							name="syncFinishTime"
							type="text"
							readOnly
							className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
							value={
								(data as DistrictClassLinkInfoResult).syncFinishTime == undefined
									? ""
									: localeShortDateTime(
											(data as DistrictClassLinkInfoResult).syncFinishTime,
											true
										)
							}
						/>
					</Field>
				</div>
			)}
		</div>
	);
}
